/**
 * Relative time formatting for the auth UI.
 *
 * Used in /sessions where the relevant question is "is this me right now?",
 * not the absolute timestamp. Falls back to a localized absolute date once
 * the value is older than ~30 days, since "32 days ago" reads worse than
 * "Apr 5, 2026".
 */
const rtf = new Intl.RelativeTimeFormat(undefined, { numeric: "auto" });

const MINUTE = 60_000;
const HOUR = 60 * MINUTE;
const DAY = 24 * HOUR;
const WEEK = 7 * DAY;

const absoluteDate = new Intl.DateTimeFormat(undefined, {
  dateStyle: "medium",
  timeStyle: "short",
});

export function formatRelative(value: string | Date, now = Date.now()): string {
  const ts = typeof value === "string" ? new Date(value).getTime() : value.getTime();
  if (!Number.isFinite(ts)) return "";

  const diff = ts - now;
  const abs = Math.abs(diff);

  if (abs < MINUTE) return "just now";
  if (abs < HOUR) return rtf.format(Math.round(diff / MINUTE), "minute");
  if (abs < DAY) return rtf.format(Math.round(diff / HOUR), "hour");
  if (abs < WEEK) return rtf.format(Math.round(diff / DAY), "day");
  if (abs < 30 * DAY) return rtf.format(Math.round(diff / WEEK), "week");
  return absoluteDate.format(ts);
}
